import { StyledContent, StyledButton, StyledTitle, StyledSelect } from '@/components/styles';
import addSoftHyphenation from '@/lib/addSoftHyphenation';
import genLinkParams from '@/lib/generateLinkParams';
import { animateScroll as scroll } from 'react-scroll';
import { useState } from 'react';
import Link from 'next/link';
import { Anchors, Appointment, Certificate, ContentSection, Breadcrumbs, Picture, Review, Portfolio, Hero, Social, VideoLink, SelectBase, FeedBack } from '@/components';
import { useTranslation } from 'react-i18next';

const AppointmentView = (props) => {
  const { t } = useTranslation('global');
  return (
    <StyledButton size="max" light arrow {...props}>
      {t('bookAppointment')}
    </StyledButton>
  );
};

export default function SpecialistPage(props) {
  const {
    id,
    slug,
    first_name,
    last_name,
    patronymic,
    position,
    qualification_desc,
    content: htmlContent,
    image: imageFromProps,
    portfolio,
    reviews,
    certificates,
    services,
    banner,
    instagram_link,
    fb_link,
    youtube_link,
    show_banner,
  } = props;
  const { t } = useTranslation('global');
  const [activeBlock, setActiveBlock] = useState('all');
  const [href, as] = genLinkParams({ type: 'specialist', slug });

  const image = imageFromProps || {};
  const title = `${last_name} ${first_name}${patronymic ? ' ' + patronymic : ''}`;

  const bannerImage = {
    desktop: banner?.main_page_desktop,
    tablet: banner?.main_page_tablet,
    mobile: banner?.main_page_mobile,
  };

  const specialistBanner = {
    id: slug,
    title,
    short_content: position,
    image: bannerImage,
    theme: 'dark',
  };

  const breadcrumbs = [
    {
      id: 1,
      title: t('main.titlePage'),
      slug: '',
      type: 'index',
    },
    {
      id: 2,
      title: t('specialists.titlePage'),
      slug: '',
      type: 'specialists',
    },
    {
      id: 3,
      title: title,
      slug: '',
      type: 'specialist',
    },
  ];

  const socialList = {};
  if (instagram_link) socialList.insta = instagram_link;
  if (fb_link) socialList.fb = fb_link;

  const anchors = [];

  if (qualification_desc) {
    anchors.push({ id: 'qualification', title: t('specialists.qualificationsTitle'), slug: 'qualification' });
  }
  if (certificates && certificates.length) {
    anchors.push({ id: 'certificates', title: t('specialists.certificatesTitle'), slug: 'certificates' });
  }
  if (portfolio && portfolio.length) {
    anchors.push({ id: 'portfolio', title: t('specialists.portfolioTitle'), slug: 'portfolio' });
  }
  if (reviews && reviews.length) {
    anchors.push({ id: 'reviews', title: t('specialists.reviewsTitle'), slug: 'reviews' });
  }

  const handleClick = (id) => {
    setActiveBlock(id);
    scroll.scrollToTop({
      duration: 0,
      smooth: false,
    });
  };

  const directions = new Set();
  if (services.length) {
    services.forEach(({ direction }) => directions.add(JSON.stringify(direction)));
  }

  return (
    <>
      {show_banner && <Hero data={[specialistBanner]} size="l-small" hiddenTablet={activeBlock !== 'all'} />}
      <StyledContent pt="none">
        <StyledContent.Container fullWidth>
          <StyledContent.Columns half reverse isAnchorsVisible firstBlock={activeBlock === 'all'}>
            <StyledContent.Side hiddenTablet={activeBlock !== 'all'}>
              <StyledContent.BreadcrumbsContainer spec>{breadcrumbs?.length > 0 && <Breadcrumbs items={breadcrumbs} theme="light" />}</StyledContent.BreadcrumbsContainer>
              <StyledTitle size="large" as="h1" mb="small" onClick={() => setActiveBlock('all')}>
                <Link href={href} as={as} passHref>
                  {addSoftHyphenation(title, 15)}
                </Link>
              </StyledTitle>
              <StyledContent.SpecialistInfo>{position}</StyledContent.SpecialistInfo>
              {htmlContent && (
                <ContentSection pt="none" pb="x-small" noTitle>
                  <StyledContent.Description>
                    <StyledContent.Html size="medium" black dangerouslySetInnerHTML={{ __html: htmlContent }} />
                  </StyledContent.Description>
                </ContentSection>
              )}
              {services && services.length > 0 && (
                <ContentSection pt="none" pb="x-small" noTitle>
                  <StyledContent.SpecialistSubtitle>{t('specialists.servicesTitle')}</StyledContent.SpecialistSubtitle>
                  {!![...directions].length && services.length > 9 ? (
                    [...directions].map((direction) => JSON.parse(direction)).map(direction => {
                      return (
                          <SelectBase key={direction.id} title={direction.title} closeOnClick={true}>
                            {services.filter(service => service.direction.id == direction.id).map((service) => {
                              const [serviceHref, serviceAs] = genLinkParams({ type: 'service', slug: service.slug, parentSlug: direction.slug });
                              return (
                                <StyledSelect.Item key={service.id}>
                                  <Link href={serviceHref} as={serviceAs}>
                                  <StyledSelect.RadioTitle>{service.title}</StyledSelect.RadioTitle>
                                  </Link>
                                </StyledSelect.Item>
                              );
                            })}
                          </SelectBase>
                      );
                    })
                  ) : (
                    <StyledContent.SpecialistServices>
                      {services.map((service) => {
                        const { id, slug, title, direction } = service;
                        const [serviceHref, serviceAs] = genLinkParams({ type: 'service', slug, parentSlug: direction.slug });
                        return (
                          <Link href={serviceHref} as={serviceAs} key={id}>
                            <StyledButton lightFilled size="mid">
                              {title}
                            </StyledButton>
                          </Link>
                        );
                      })}
                    </StyledContent.SpecialistServices>
                  )}
                </ContentSection>
              )}
              {!!anchors?.length && <Anchors list={anchors} active={activeBlock || 'all'} handleClick={handleClick} type="specialist" arrow="left" />}
              <StyledContent.Button>
                <Appointment render={AppointmentView} />
              </StyledContent.Button>
            </StyledContent.Side>
            <StyledContent.ColumnMain bg={activeBlock !== 'all'}>
              <StyledContent.Loadable>
                {activeBlock === 'all' && Object.keys(image).length > 0 && (
                  <ContentSection pt="none" pb="none" type="block">
                    <StyledContent.Img fullsize>
                      <Picture {...image} />
                    </StyledContent.Img>
                    {(Object.keys(socialList).length > 0 || youtube_link) && (
                      <StyledContent.SocialList absolute>
                        {Object.keys(socialList).map((item) => (
                          <Social key={item} icon={item} href={socialList[item]} />
                        ))}
                        {youtube_link && <VideoLink key="yb" href={youtube_link} title="" />}
                      </StyledContent.SocialList>
                    )}
                  </ContentSection>
                )}
                {activeBlock === 'qualification' && qualification_desc && (
                  <>
                    <StyledContent.BackLink onClick={() => setActiveBlock('all')}>{t('specialists.qualificationsTitle')}</StyledContent.BackLink>
                    <ContentSection pt="none" pb="none" type="qualification" title={t('specialists.qualificationsTitle')}>
                      <StyledContent.Description type="qualification">
                        <StyledContent.Html black dangerouslySetInnerHTML={{ __html: qualification_desc }} />
                      </StyledContent.Description>
                    </ContentSection>
                  </>
                )}
                {activeBlock === 'certificates' && certificates && certificates.length > 0 && (
                  <>
                    <StyledContent.BackLink onClick={() => setActiveBlock('all')}>{t('specialists.certificatesTitle')}</StyledContent.BackLink>
                    <ContentSection pt="none" pb="none" type="certificates" title={t('specialists.certificatesTitle')}>
                      <StyledContent.Certificates>
                        {certificates.map((certificate) => (
                          <Certificate key={certificate.id} certificate={certificate} />
                        ))}
                      </StyledContent.Certificates>
                    </ContentSection>
                  </>
                )}
                {activeBlock === 'portfolio' && portfolio && portfolio.length > 0 && (
                  <>
                    <StyledContent.BackLink onClick={() => setActiveBlock('all')}>{t('specialists.portfolioTitle')}</StyledContent.BackLink>
                    <ContentSection pt="none" pb="none" type="portfolio" title={t('specialists.portfolioTitle')}>
                      <Portfolio data={portfolio} />
                    </ContentSection>
                  </>
                )}
                {activeBlock === 'reviews' && reviews && reviews.length > 0 && (
                  <>
                    <StyledContent.BackLink onClick={() => setActiveBlock('all')}>{t('specialists.reviewsTitle')}</StyledContent.BackLink>
                    
                   
                    <ContentSection pt="none" pb="none" type="reviews" title={t('specialists.reviewsTitle')} HeaderComponent={() => <FeedBack id={id} />}>
                      {reviews.map((review) => {
                        const { id, ...otherProps } = review;
                        return <Review key={id} {...otherProps} p="mid" />;
                      })}
                    </ContentSection>
                  </>
                )}
              </StyledContent.Loadable>
            </StyledContent.ColumnMain>
          </StyledContent.Columns>
        </StyledContent.Container>
      </StyledContent>
    </>
  );
}
